// src/pages/Login.jsx — Epic Games Store style
import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { login, resetPassword } from "../services/authService";

const S = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
*, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }

.auth-pg {
  min-height:100vh; background:#121212;
  display:flex; font-family:'Inter',sans-serif;
}

/* Left panel */
.auth-left {
  flex:1; display:flex; flex-direction:column; align-items:center; justify-content:center;
  padding:48px 40px; position:relative; overflow:hidden;
}
.auth-left-bg {
  position:absolute; inset:0;
  background:linear-gradient(135deg,#0d1b2e 0%,#121212 60%);
}
.auth-left-glow {
  position:absolute; top:-10%; left:-10%; width:60%; padding-top:60%;
  background:radial-gradient(circle, rgba(0,120,242,0.18) 0%,transparent 70%);
  pointer-events:none;
}
.auth-left-grid {
  position:absolute; inset:0;
  background-image:
    linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px);
  background-size:40px 40px;
}
.auth-left-content { position:relative; z-index:2; max-width:360px; width:100%; }

/* Logo */
.auth-logo { display:flex; align-items:center; gap:10px; margin-bottom:56px; }
.auth-logo-icon { width:38px; height:38px; background:#0078F2; border-radius:8px; display:flex; align-items:center; justify-content:center; font-size:19px; }
.auth-logo-name { font-size:18px; font-weight:800; color:#fff; letter-spacing:-0.3px; }
.auth-logo-name span { color:#0078F2; }

/* Form card */
.auth-card {
  background:#1a1a1a; border:1px solid rgba(255,255,255,0.08); border-radius:12px;
  padding:40px 36px;
  animation:cardIn 0.35s cubic-bezier(0.16,1,0.3,1);
}
@keyframes cardIn { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }

.auth-card-title { font-size:24px; font-weight:800; color:#fff; letter-spacing:-0.5px; margin-bottom:6px; }
.auth-card-sub   { font-size:14px; color:#6b6b6b; font-weight:400; margin-bottom:32px; }

/* Fields */
.field { margin-bottom:18px; }
.field-label { display:block; font-size:12px; font-weight:600; color:#6b6b6b; margin-bottom:8px; letter-spacing:0; text-transform:none; }
.input-wrap { position:relative; }
.field-inp {
  width:100%; background:#121212; border:1px solid rgba(255,255,255,0.1); border-radius:8px;
  padding:13px 16px; font-family:'Inter',sans-serif; font-size:14px; font-weight:400; color:#fff;
  outline:none; transition:all 0.15s;
}
.field-inp:focus { border-color:#0078F2; background:rgba(0,120,242,0.04); box-shadow:0 0 0 3px rgba(0,120,242,0.1); }
.field-inp::placeholder { color:#3a3a3a; }
.field-inp.pr { padding-right:48px; }
.pw-toggle { position:absolute; right:14px; top:50%; transform:translateY(-50%); background:none; border:none; color:#4a4a4a; cursor:pointer; font-size:16px; padding:2px; transition:color 0.15s; }
.pw-toggle:hover { color:#a3a3a3; }

.forgot-row { text-align:right; margin-top:-10px; margin-bottom:20px; }
.btn-text { background:none; border:none; font-family:'Inter',sans-serif; font-size:13px; font-weight:500; color:#0078F2; cursor:pointer; padding:0; transition:color 0.15s; }
.btn-text:hover { color:#0a84ff; }

/* Alerts */
.alert { display:flex; align-items:flex-start; gap:10px; padding:12px 14px; border-radius:8px; font-size:13px; font-weight:500; margin-bottom:18px; line-height:1.5; }
.alert-error   { background:rgba(239,68,68,0.08);  border:1px solid rgba(239,68,68,0.2);  color:#f87171; }
.alert-success { background:rgba(34,197,94,0.08); border:1px solid rgba(34,197,94,0.2); color:#4ade80; }

/* Submit */
.btn-submit {
  width:100%; padding:14px; background:#0078F2; border:none; border-radius:8px;
  font-family:'Inter',sans-serif; font-size:15px; font-weight:700; color:#fff;
  cursor:pointer; transition:all 0.15s; margin-top:6px; letter-spacing:0;
}
.btn-submit:hover { background:#0a84ff; transform:translateY(-1px); }
.btn-submit:active { transform:translateY(0); }
.btn-submit:disabled { background:#2a2a2a; color:#4a4a4a; cursor:not-allowed; transform:none; }

.auth-footer { text-align:center; margin-top:20px; font-size:14px; color:#6b6b6b; }
.auth-footer a { color:#0078F2; text-decoration:none; font-weight:600; }
.auth-footer a:hover { color:#0a84ff; }

.back-link { display:inline-flex; align-items:center; gap:5px; font-size:13px; font-weight:500; color:#6b6b6b; cursor:pointer; background:none; border:none; margin-bottom:20px; transition:color 0.15s; font-family:'Inter',sans-serif; }
.back-link:hover { color:#fff; }

/* Right panel — decorative */
.auth-right {
  width:420px; flex-shrink:0;
  background:#0d1118; border-left:1px solid rgba(255,255,255,0.06);
  display:flex; flex-direction:column; align-items:center; justify-content:center; padding:48px;
  position:relative; overflow:hidden;
}
.auth-right-glow {
  position:absolute; bottom:-10%; right:-10%; width:70%; padding-top:70%;
  background:radial-gradient(circle, rgba(0,120,242,0.12) 0%,transparent 70%);
  pointer-events:none;
}
.auth-right-content { position:relative; z-index:2; text-align:center; }
.auth-right-heading { font-size:28px; font-weight:900; color:#fff; letter-spacing:-0.8px; margin-bottom:12px; line-height:1.2; }
.auth-right-heading span { color:#0078F2; }
.auth-right-sub { font-size:14px; color:#4a4a4a; line-height:1.7; max-width:280px; margin:0 auto 40px; }
.benefit-list { display:flex; flex-direction:column; gap:14px; text-align:left; }
.benefit { display:flex; align-items:center; gap:12px; }
.benefit-icon { width:36px; height:36px; border-radius:8px; display:flex; align-items:center; justify-content:center; font-size:16px; flex-shrink:0; }
.benefit-text { font-size:14px; font-weight:500; color:#a3a3a3; }
.benefit-text strong { color:#e0e0e0; font-weight:600; }

@media(max-width:900px) { .auth-right { display:none; } }
@media(max-width:480px) { .auth-left { padding:32px 20px; } .auth-card { padding:28px 22px; } .auth-logo { margin-bottom:36px; } }
`;

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetSent, setResetSent] = useState(false);
  const [showPw, setShowPw] = useState(false);

  async function handleLogin(e) {
    e.preventDefault(); setError(""); setLoading(true);
    try { await login(email, password); navigate(from, { replace:true }); }
    catch (err) { setError(getFriendlyError(err.code)); }
    finally { setLoading(false); }
  }

  async function handleReset(e) {
    e.preventDefault(); setError(""); setLoading(true);
    try { await resetPassword(resetEmail); setResetSent(true); }
    catch (err) { setError(getFriendlyError(err.code)); }
    finally { setLoading(false); }
  }

  function getFriendlyError(code) {
    switch(code) {
      case "auth/invalid-credential":
      case "auth/wrong-password":
      case "auth/user-not-found": return "Invalid email or password. Please try again.";
      case "auth/too-many-requests": return "Too many attempts. Please try again later.";
      case "auth/invalid-email":    return "Please enter a valid email address.";
      default: return "Something went wrong. Please try again.";
    }
  }

  return (
    <>
      <style>{S}</style>
      <div className="auth-pg">
        {/* Left — form */}
        <div className="auth-left">
          <div className="auth-left-bg" />
          <div className="auth-left-grid" />
          <div className="auth-left-glow" />
          <div className="auth-left-content">
            <div className="auth-logo">
              <div className="auth-logo-icon">🎮</div>
              <div className="auth-logo-name">Phsar<span>Game</span></div>
            </div>

            <div className="auth-card">
              {!showReset ? (
                <>
                  <div className="auth-card-title">Welcome back</div>
                  <div className="auth-card-sub">Sign in to your account</div>

                  {error && <div className="alert alert-error">⚠️ {error}</div>}

                  <form onSubmit={handleLogin}>
                    <div className="field">
                      <label className="field-label">Email address</label>
                      <input className="field-inp" type="email" placeholder="your@email.com" value={email} onChange={e=>setEmail(e.target.value)} required autoComplete="email" />
                    </div>
                    <div className="field">
                      <label className="field-label">Password</label>
                      <div className="input-wrap">
                        <input className="field-inp pr" type={showPw?"text":"password"} placeholder="••••••••" value={password} onChange={e=>setPassword(e.target.value)} required autoComplete="current-password" />
                        <button type="button" className="pw-toggle" onClick={()=>setShowPw(!showPw)}>{showPw?"🙈":"👁️"}</button>
                      </div>
                    </div>
                    <div className="forgot-row">
                      <button type="button" className="btn-text" onClick={()=>{setShowReset(true);setError("");}}>Forgot password?</button>
                    </div>
                    <button type="submit" className="btn-submit" disabled={loading}>
                      {loading ? "Signing in…" : "Sign In →"}
                    </button>
                  </form>

                  <div className="auth-footer">
                    Don't have an account? <Link to="/register">Create one</Link>
                  </div>
                </>
              ) : (
                <>
                  <button className="back-link" onClick={()=>{setShowReset(false);setError("");setResetSent(false);}}>← Back to sign in</button>
                  <div className="auth-card-title">Reset password</div>
                  <div className="auth-card-sub">We'll send a reset link to your email</div>

                  {error && <div className="alert alert-error">⚠️ {error}</div>}
                  {resetSent && <div className="alert alert-success">✅ Reset link sent! Check your inbox.</div>}

                  {!resetSent && (
                    <form onSubmit={handleReset}>
                      <div className="field">
                        <label className="field-label">Email address</label>
                        <input className="field-inp" type="email" placeholder="your@email.com" value={resetEmail} onChange={e=>setResetEmail(e.target.value)} required />
                      </div>
                      <button type="submit" className="btn-submit" disabled={loading}>
                        {loading ? "Sending…" : "Send Reset Link →"}
                      </button>
                    </form>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Right — decorative */}
        <div className="auth-right">
          <div className="auth-right-glow" />
          <div className="auth-right-content">
            <div className="auth-right-heading">Play Premium<br />Games in <span>Cambodia</span></div>
            <div className="auth-right-sub">Access 100+ Steam games at up to 90% off official prices.</div>
            <div className="benefit-list">
              {[
                { icon:"⚡", bg:"rgba(0,120,242,0.12)", text:<><strong>Instant Delivery</strong> — credentials in seconds</> },
                { icon:"🔒", bg:"rgba(34,197,94,0.1)",  text:<><strong>Offline Access</strong> — no internet needed to play</> },
                { icon:"💰", bg:"rgba(234,179,8,0.1)",  text:<><strong>Best Prices</strong> — starting from $2.99 USD</> },
                { icon:"💬", bg:"rgba(167,139,250,0.1)",text:<><strong>24/7 Support</strong> — live chat always available</> },
              ].map((b,i) => (
                <div key={i} className="benefit">
                  <div className="benefit-icon" style={{background:b.bg}}>{b.icon}</div>
                  <div className="benefit-text">{b.text}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}